import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import type { Provider, StoreItem, Wish } from "@starquest/core";
import { useTheme } from "../hooks/useTheme";
import styles from "./KidHome.module.css";
import own from "./KidStore.module.css";
import PreviewBanner from "../components/PreviewBanner";

export default function KidWishlist() {
  const navigate   = useNavigate();
  const { familyId: authFamilyId } = useAuthStore();
  const { selectedKid, storeItems, wishes, wishlistItemIds, setStoreItems, setProviders, setWishlistItemIds, setWishes } = useFamilyStore();
  const kid = selectedKid;
  const familyId = authFamilyId || kid?.familyId || null;
  const theme = useTheme(kid);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!familyId || !kid) return;
    setLoading(true);
    Promise.all([
      api.listItems(familyId).then((i) => setStoreItems(i as StoreItem[])),
      api.listProviders(familyId).then((p) => setProviders(p as Provider[])),
      api.getWishlist(familyId, kid.kidId).then((w) => setWishlistItemIds((w as { itemIds: string[] }).itemIds)),
      api.listWishes(familyId).then((w) => setWishes((w as Wish[]).filter((x) => x.kidId === kid.kidId))),
    ])
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [familyId, kid?.kidId]);

  async function removeHeart(itemId: string) {
    if (!familyId || !kid) return;
    await api.toggleWishlist(familyId, kid.kidId, itemId);
    setWishlistItemIds(wishlistItemIds.filter((id) => id !== itemId));
  }

  const heartedItems = storeItems.filter((i) => wishlistItemIds.includes(i.itemId));
  const kidWishes    = wishes.filter((w) => w.kidId === kid?.kidId);

  return (
    <div className={styles.page}>
      <PreviewBanner />
      <div className={styles.starbg} aria-hidden />

      <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative", zIndex: 2 }}>
        <button type="button" className={styles.hudBack} onClick={() => navigate("/kid/store")}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18 L9 12 L15 6"/></svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 22 }}>❤️ My Wishlist</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "rgba(240,240,255,0.5)", marginTop: 2 }}>
            {kid?.name?.toUpperCase()} · {heartedItems.length} SAVED · {kidWishes.filter((w) => w.status === "pending").length} PENDING
          </div>
        </div>
        {kid && (
          <div className={styles.starBal}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--gold)"><path d="M12 2 L14.6 9.4 L22 10 L16.3 14.8 L18.2 22 L12 17.8 L5.8 22 L7.7 14.8 L2 10 L9.4 9.4 Z"/></svg>
            {kid.pointBalance}
          </div>
        )}
      </div>

      <div className={own.body}>
        {loadError && (
          <div style={{
            background: "rgba(255,107,107,0.12)", border: "1px solid rgba(255,107,107,0.3)",
            borderRadius: 12, padding: "10px 16px", fontSize: 13, color: "#ff8080",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            ⚠️ Couldn't load your wishlist. Try going back and opening it again.
          </div>
        )}
        {loading && <div className={own.loading}>Loading wishlist…</div>}

        {!loading && (
          <>
            <div className={own.shelfLabel}>❤️ SAVED FROM STORE</div>
            {heartedItems.length === 0
              ? <div className={own.wishEmpty}>No saved items yet — heart items in the store to save them here.</div>
              : heartedItems.map((item) => {
                  const canAfford = kid ? kid.pointBalance >= item.pointCost : false;
                  return (
                    <div key={item.itemId} className={own.wishlistRow}>
                      <span className={own.itemEmoji} style={{ fontSize: 28 }}>{item.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div className={own.itemName}>{item.name}</div>
                        <div className={own.itemCost}>
                          {theme.pointEmoji} {item.pointCost}
                          {!canAfford && <span style={{ color: "rgba(240,240,255,0.45)", fontWeight: 400 }}> · need {item.pointCost - (kid?.pointBalance ?? 0)} more</span>}
                        </div>
                      </div>
                      <button type="button" className={`${own.heartBtn} ${own.hearted}`} onClick={() => removeHeart(item.itemId)}>♥</button>
                    </div>
                  );
                })
            }

            <div className={own.shelfLabel} style={{ marginTop: 20 }}>🌟 MY DREAM WISHES</div>
            {kidWishes.length === 0
              ? <div className={own.wishEmpty}>No wishes yet — tap "🌟 Wish" in the store to send one to your {theme.parentRole}.</div>
              : kidWishes.map((w) => (
                  <div key={w.wishId} className={own.wishlistRow} style={{
                    borderColor: w.status === "approved" ? "rgba(72,199,142,0.3)" : w.status === "declined" ? "rgba(255,107,107,0.3)" : "rgba(255,215,0,0.2)",
                    background:  w.status === "approved" ? "rgba(72,199,142,0.05)" : w.status === "declined" ? "rgba(255,107,107,0.05)" : "rgba(255,215,0,0.04)",
                  }}>
                    <span style={{ fontSize: 24 }}>🌟</span>
                    <div style={{ flex: 1 }}>
                      <div className={own.itemName}>{w.name}</div>
                      <div className={own.itemDesc}>
                        {w.status === "pending"  && `⏳ Waiting for ${theme.parentRole}`}
                        {w.status === "approved" && "✅ Added to store!"}
                        {w.status === "declined" && "❌ Not available right now"}
                      </div>
                    </div>
                  </div>
                ))
            }
          </>
        )}
      </div>

      <nav className={styles.nav}>
        <button type="button" className={styles.navBtn} onClick={() => navigate("/kid")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 11 L12 3 L21 11"/><path d="M5 10 V21 H19 V10"/></svg>
          Home
        </button>
        <button type="button" className={styles.navBtn} onClick={() => navigate("/kid/quest-log")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9 H21"/></svg>
          {theme.challengeLogTitle}
        </button>
        <button type="button" className={styles.navBtn} onClick={() => navigate("/kid/cashout")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 21 L21 21"/><path d="M3 10 L12 3 L21 10"/></svg>
          {theme.pointBankTitle}
        </button>
        <button type="button" className={`${styles.navBtn} ${styles.navActive}`} onClick={() => navigate("/kid/store")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 7 L21 7 L20 20 L4 20 Z"/><path d="M9 7 V5 A3 3 0 0 1 15 5 V7"/></svg>
          Store
        </button>
      </nav>
    </div>
  );
}
